import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useState } from 'react';
import { getPanelIconPath } from '../../../data/panelIconCatalog';
import HomeButtonImage from './HomeButtonImage';

export default function ShortcutTile({
  shortcut,
  canDrag,
  canEdit = true,
  suppressOpen,
  allowOpen = true,
  onOpen,
  onEdit,
  tenantId = '',
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: shortcut.id,
    disabled: !canDrag,
  });

  const iconPath = getPanelIconPath(shortcut.iconKey) || getPanelIconPath('website');

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    touchAction: 'pan-y',
    cursor: canDrag ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
    zIndex: isDragging ? 2 : 'auto',
  };

  const openLink = () => {
    if (!allowOpen || suppressOpen || isDragging) return;
    onOpen?.(shortcut);
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{ position: 'relative', minWidth: 0 }}
      {...attributes}
      {...(canDrag ? listeners : {})}
    >
      <Box
        component="button"
        type="button"
        aria-label={`${shortcut.label}. Open in browser`}
        onClick={openLink}
        onContextMenu={event => {
          event.preventDefault();
          event.stopPropagation();
          if (canEdit) onEdit(shortcut);
        }}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openLink();
          }
          if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) {
            event.preventDefault();
            if (canEdit) onEdit(shortcut);
          }
        }}
        sx={{
          appearance: 'none',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: '#fff',
          width: '100%',
          minWidth: 0,
          borderRadius: 2,
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.75,
          boxShadow: isDragging ? '0 8px 18px rgba(15, 23, 42, 0.16)' : 'none',
        }}
      >
        {shortcut.iconType === 'image' && shortcut.iconImageAssetId ? (
          <HomeButtonImage
            assetId={shortcut.iconImageAssetId}
            tenantId={tenantId}
            alt=""
            fallback={
              <Box
                component="img"
                src={iconPath}
                alt=""
                sx={{ width: 56, height: 56, objectFit: 'contain', pointerEvents: 'none' }}
              />
            }
            sx={{ width: 56, height: 56, pointerEvents: 'none' }}
          />
        ) : (
          <Box
            component="img"
            src={iconPath}
            alt=""
            sx={{ width: 56, height: 56, objectFit: 'contain', pointerEvents: 'none' }}
          />
        )}
        <Typography
          variant="caption"
          sx={{
            width: '100%',
            textAlign: 'center',
            lineHeight: 1.2,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            color: 'text.primary',
          }}
        >
          {shortcut.label}
        </Typography>
      </Box>
      <IconButton
        size="small"
        aria-label={`Shortcut options for ${shortcut.label}`}
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          setMenuAnchor(event.currentTarget);
        }}
        onPointerDown={event => event.stopPropagation()}
        sx={{ position: 'absolute', top: 0, right: 0 }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        onClick={event => event.stopPropagation()}
      >
        {allowOpen ? (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onOpen?.(shortcut);
            }}
          >
            Open in browser
          </MenuItem>
        ) : null}
        {canEdit ? (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onEdit(shortcut);
            }}
          >
            Edit
          </MenuItem>
        ) : null}
      </Menu>
    </Box>
  );
}
